import {useForm} from 'antd/es/form/Form';
import {useCallback, useEffect, useMemo, useRef, useState, ReactNode} from 'react';

import {IModalOpenerComponentRef} from 'components/modal-opener-component';
import {resetLoadedData, selectEntityData} from 'modules/data';
import {actionRequestData} from 'modules/data/data-actions';
import {ITableEntityData, TEntity} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {IAntdErrorField} from 'shared/types/antd';
import {TStateSetter} from 'shared/types/generics';
import {ERequestType} from 'shared/types/requests';
import {showMessage, showMessageFromResponse} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';

import {fetchFactorInfluenceInfoItem} from '../api';
import {mapFormDataToRequest, mapResponseToFormData, getTabNameByFieldName} from '../lib';
import {
    FACTOR_INFLUENCE_INFO_ITEM_MODAL_TAB_KEYS,
    FACTORS_REQUIRED_ERROR_MESSAGE,
    PRODUCTS_REQUIRED_ERROR_MESSAGE,
} from '../model/constants';
import {IDocumentDetails, IMacroEconomicItemForm} from '../model/types';

interface IUseFactorInfluenceInfoItemModalArgs {
    entityName: string;
    wrapper?: ReactNode;
    record?: TEntity;
    tabKey?: string;
}

export const useFactorInfluenceInfoItemModal = ({entityName, record, tabKey}: IUseFactorInfluenceInfoItemModalArgs) => {
    const [activeTab, setActiveTab] = useState<string>(tabKey || FACTOR_INFLUENCE_INFO_ITEM_MODAL_TAB_KEYS.MAIN);
    const [effectInfluence, setEffectInfluence] = useState<number | undefined>();
    const [form] = useForm<IMacroEconomicItemForm>();
    const [isLoading, setIsLoading] = useState(false);
    const modalRef = useRef<IModalOpenerComponentRef>(null);
    const dispatch = useAppDispatch();
    const tableEntityData = useAppSelector(selectEntityData(entityName, EEntityType.TABLE)) as ITableEntityData & {
        selectedRows: IDocumentDetails[];
    };
    const selectedRows = useMemo(
        () => (tableEntityData && tableEntityData?.selectedRows ? tableEntityData?.selectedRows : []),
        [tableEntityData]
    );
    const [isOpen, setIsOpen] = useState(false);
    const [isFormFetched, setIsFormFetched] = useState(false);

    const loadData = useCallback(async () => {
        if (!record?.id || isFormFetched) {
            return;
        }

        try {
            const response = await fetchFactorInfluenceInfoItem({id: record.id as string});

            if (response?.status !== 200 || !response?.data) {
                return;
            }

            const data = mapResponseToFormData(response.data);
            setIsFormFetched(true);
            form.setFieldsValue(data);
            setEffectInfluence(data?.effectInfluenceId || undefined);
        } catch (e) {
            if (e.response) {
                showMessageFromResponse({response: e, isError: true});
            } else {
                showMessage({message: 'Произошла ошибка при загрузке формы', isError: true});
            }
            modalRef.current?.setIsModalOpen(false);
            form.resetFields();
        }
    }, [record, form, isFormFetched, setIsFormFetched]);

    useEffect(() => {
        if (!isOpen || !record?.id || isFormFetched) {
            return;
        }

        setActiveTab(tabKey || FACTOR_INFLUENCE_INFO_ITEM_MODAL_TAB_KEYS.MAIN);
        setIsLoading(true);
        loadData().then(() => {
            setIsLoading(false);
        });
    }, [isOpen, record, loadData, tabKey, isFormFetched]);

    const onCloseEvents = useCallback(() => {
        setActiveTab(FACTOR_INFLUENCE_INFO_ITEM_MODAL_TAB_KEYS.MAIN);
        setEffectInfluence(undefined);
        setIsFormFetched(false);
        form.resetFields();
    }, [form]);

    const handleCloseModal = useCallback(
        (setIsModalOpen: TStateSetter<boolean>) => {
            onCloseEvents();
            setIsModalOpen(false);
        },
        [onCloseEvents]
    );

    const handleFinish = useCallback(() => {
        const fieldsData = form.getFieldsValue();
        const data = mapFormDataToRequest(fieldsData);
        const idPath = record ? `/${record?.id}` : '';

        actionRequestData({
            requestUrl: `integration.module/ais3.factor.influence.info${idPath}`,
            requestType: record ? ERequestType.PUT : ERequestType.POST,
            successMessage: 'Данные сохранены',
            options: {isJsonRequest: true},
            data,
        }).then((response: any) => {
            if (response?.status === 200) {
                modalRef.current?.setIsModalOpen(false);
                onCloseEvents();
                dispatch(resetLoadedData(entityName, EEntityType.TABLE));
            }
        });
    }, [form, selectedRows, modalRef, onCloseEvents, record]);

    const handleSave = useCallback(async () => {
        try {
            await form.validateFields();
            form.submit();
        } catch (errorInfo: any) {
            const errorFields = errorInfo?.errorFields || [];
            if (errorFields.length === 0) {
                return;
            }
            
            const hasProductsError = errorFields.some((errorField: IAntdErrorField) => {
                const fieldName = errorField.name;
                return Array.isArray(fieldName) && 
                    fieldName.length === 3 && 
                    fieldName[0] === 'factors' && 
                    fieldName[2] === 'prods';
            });
            
            if (hasProductsError) {
                showMessage({
                    message: PRODUCTS_REQUIRED_ERROR_MESSAGE,
                    isError: true,
                });
                return;
            }
            
            const hasFactorError = errorFields.some((errorField: IAntdErrorField) => {
                const fieldName = errorField.name;
                return fieldName[0] === 'factors';
            });
            
            if (hasFactorError) {
                showMessage({
                    message: FACTORS_REQUIRED_ERROR_MESSAGE,
                    isError: true,
                });
                return;
            }
            
            const firstErrorFieldName = errorFields[0]?.name;
            const tabName = getTabNameByFieldName(firstErrorFieldName);
            showMessage({
                message: `Не заполнены обязательные поля во вкладке "${tabName}"`,
                isError: true,
            });
        }
    }, [form]);

    return {
        modalRef,
        isOpen,
        form,
        isLoading,
        activeTab,
        setActiveTab,
        effectInfluence,
        setEffectInfluence,
        setIsOpen,
        handleCloseModal,
        handleFinish,
        handleSave,
    };
};
